import React, {useCallback, useMemo, useState} from "react";
import {useDispatch} from "react-redux";
import {useTranslation} from "react-i18next";
import {Chip, CircularProgress, SvgIcon} from "@mui/material";
import {Edit, OpenInNew} from "@mui/icons-material";
import {Col, Row} from "reactstrap";
import IconButton from "../../base/button/icon_button";
import DraggableSwitch from "../../base/switch/draggable-switch";
import DateUtils from "../../../../core/services/date-utils";
import {ReactComponent as RemoveIcon} from "../../../../assets/images/remove.svg";
import {ReactComponent as Person} from "../../../../assets/images/sidebar/user.svg";
import {ReactComponent as DateTime} from "../../../../assets/images/date-time-icon.svg";
import useIsMounted from "../../../hooks/use-is-mounted";
import {useOperatorInformation} from "../../../hooks/use-operator-information";
import useNotebookTagsInfo from "../../../hooks/use-note-book-tags-info";
import {setReduxDialogState} from "../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../core/models/static/routes/redux-store-routes";


const CustomerNoteCard = ({
    note,
    toggleIsPrivate,
    setNote,
    removeNote,
    showSource = true,
}) => {
    const [updating, setUpdating] = useState(false);
    const [removing, setRemoving] = useState(false);
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const operatorInformation = useOperatorInformation();
    const noteTags = useMemo(() => note?.tagList ?? note?.tags ?? [], [note?.tagList, note?.tags]);
    const {pageInfo, loading: pageInfoLoading} = useNotebookTagsInfo(noteTags);
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.customer-note-card'});

    const ownerOfNote = useMemo(
        () => note?.createdBy === operatorInformation?.id,
        [note?.createdBy, operatorInformation?.id],
    );

    /**
     * Opens the source page for this note when available.
     */
    const openSourcePage = useCallback(() => {
        if (!pageInfo) {
            return;
        }
        if (pageInfo.url) {
            window.open(window.location.origin + pageInfo.url, '_blank');
            return;
        }
        pageInfo.action?.();
    }, [pageInfo]);

    /**
     * Toggles the note's isPrivate property.
     */
    const toggleNoteIsPrivate = async (value) => {
        setUpdating(true);
        await toggleIsPrivate(note, !value);
        if (!isMounted()) {
            return;
        }
        setUpdating(false);
    };

    /**
     * Opens the update note dialog.
     */
    const openUpdateNoteDialog = () => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.upsertNote, {
            open: true,
            note: note,
            onSuccess: (updatedNote) => setNote(note.id, updatedNote),
        }));
    };

    /**
     * Confirms and removes the note.
     */
    const confirmRemoval = useCallback(() => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    {t('removeConfirmation')}
                </p>
            </div>,
            {maxWidth: 'sm'},
            async (decision) => {
                if (!decision) {
                    return;
                }
                setRemoving(true);
                await removeNote(note?.id);
                if (!isMounted()) {
                    return;
                }
                setRemoving(false);
            },
        );
    }, [isMounted, note?.id, removeNote, t]);

    return (
        <div className={'customer-note-card'}>
            <div className={'header'}>
                <div className={'d-flex w-100 justify-content-between align-items-start'}>
                    <p className={'title flex-grow-1'}>
                        {note?.title ?? ''}
                    </p>
                    {
                        ownerOfNote &&
                        <div className={'d-flex align-items-center justify-content-end flex-shrink-0'}>
                            <DraggableSwitch
                                activeTitle={t('public')}
                                inActiveTitle={t('private')}
                                size={4}
                                disabled={updating}
                                checked={!note?.isPrivate}
                                onChange={(checked) => toggleNoteIsPrivate(checked)}
                            />
                            <IconButton
                                disabled={updating || removing}
                                className={'mx-1'}
                                color={'paper.secondary'}
                                onPressed={openUpdateNoteDialog}
                            >
                                {
                                    updating
                                        ? <CircularProgress size={20} color={'secondary'}/>
                                        : <Edit className={'icon normal'}/>
                                }
                            </IconButton>
                            <IconButton
                                onPressed={confirmRemoval}
                                disabled={removing || updating}
                                color={'paper.error'}
                            >
                                {
                                    removing
                                        ? <CircularProgress size={20} color={'secondary'}/>
                                        : <SvgIcon component={RemoveIcon} className='icon normal'/>
                                }
                            </IconButton>
                        </div>
                    }
                </div>
                {
                    showSource && pageInfo?.title && !pageInfoLoading &&
                    <Chip
                        className={'ml-3 mt-1 mb-1'}
                        label={pageInfo.title}
                        deleteIcon={<OpenInNew color={'primary'} sx={{width: 20, height: 20}}/>}
                        onDelete={openSourcePage}
                        onClick={openSourcePage}
                    />
                }
                {
                    !ownerOfNote &&
                    <Chip
                        className={'ml-3 mt-1 mb-1'}
                        size={'small'}
                        label={note?.isPrivate ? t('private') : t('public')}
                        color={note?.isPrivate ? 'default' : 'primary'}
                        variant={'outlined'}
                    />
                }
                <Row noGutters className={'ml-3 align-items-center'}>
                    <Col xs={'auto'}>
                        <SvgIcon component={Person} color={'secondary'} className={'icon small'}/>
                    </Col>
                    <Col xs className={'ml-2'}>
                        <p>{note?.createdByName ?? ''}</p>
                    </Col>
                </Row>
                {
                    note?.modifiedDateTime &&
                    <Row noGutters className={'ml-3 align-items-center'}>
                        <Col xs={'auto'}>
                            <SvgIcon component={DateTime} color={'secondary'} className={'icon small'}/>
                        </Col>
                        <Col xs className={'ml-2'}>
                            <p>
                                {DateUtils.serverToLocal(note.modifiedDateTime).format(DateUtils.dateTimeFormat)}
                            </p>
                        </Col>
                    </Row>
                }
            </div>
            <div
                className={'description mt-2'}
                dangerouslySetInnerHTML={{__html: note?.body ?? ''}}
            />
        </div>
    );
};

export default CustomerNoteCard;
